import { createFileRoute, Link } from "@tanstack/react-router";
import { Helmet } from "react-helmet-async";
import { MarketingLayout } from "@/components/marketing/MarketingLayout";
import { Section } from "@/components/marketing/layout/Section";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";

export const Route = createFileRoute("/connectors/xero-case-study")({
  component: XeroCaseStudyPage,
});

const RESULTS = [
  {
    metric: "Real-time",
    label: "Xero to Salesforce synchronisation",
  },
  {
    metric: "Zero",
    label: "Duplicate data entry",
  },
  {
    metric: "80%",
    label: "Of programme participants remain employed & sober after 6 months",
  },
];

const CHALLENGES = [
  "Keeping the CRM system in sync with accounting required large amounts of re-entered data - pledges and donations captured by the Accounting team had to be manually entered again by the Donor Relationship team into Salesforce.",
  "Contact details were maintained in both Xero and Salesforce, making it impossible to know which version to trust when updates were made in only one system.",
  "Discrepancies between the accounting and donor management systems risked undermining donor trust and engagement.",
];

const SOLUTION_POINTS = [
  "Using Xero's WebHooks, a server automatically receives all Invoice activity in real time - no polling or scheduled syncing required.",
  "Each activity event is analysed according to U-Turn's requirements, and qualifying invoices are automatically transformed and pushed into Salesforce as Opportunities.",
  "All contacts are now kept in a single system with a unique ID linking back to the other, so contact information is always up to date and trustworthy.",
  "A real-time shared spreadsheet is updated with every qualifying invoice activity and resulting Opportunity, providing a full audit trail.",
];

function XeroCaseStudyPage() {
  return (
    <MarketingLayout>
      <Helmet>
        <title>
          U-Turn Homeless Ministries - Xero connector case study | Larasoft
        </title>
        <meta
          name="description"
          content="How U-Turn Homeless Ministries automated real-time Xero to Salesforce CRM synchronisation, eliminating duplicate data entry and accelerating donor engagement."
        />
        <link
          rel="canonical"
          href="https://larasoft.global/connectors/xero-case-study"
        />
      </Helmet>

      {/* Hero */}
      <Section
        containerClassName="grid grid-cols-1 items-center gap-10 md:grid-cols-[1fr_1fr]"
        className="py-20"
      >
        <div className="space-y-6">
          <div className="flex flex-wrap gap-2">
            <Badge variant="info">Case Study</Badge>
            <Badge variant="outline">Xero Connector</Badge>
          </div>
          <h1 className="text-3xl font-extrabold leading-tight sm:text-4xl md:text-5xl">
            Creating CRM flows from accounting activity
          </h1>
          <p className="max-w-xl text-slate-600">
            How a Cape Town NGO accelerated their donor engagement by
            automating real-time Xero to Salesforce synchronisation -
            eliminating duplicate data entry and ensuring donor records are
            always accurate.
          </p>
          <div className="flex flex-wrap gap-3">
            <Button asChild variant="secondary">
              <Link to="/contact">Get started</Link>
            </Button>
            <Button variant="outline" asChild>
              <Link to="/connectors/product">View all connectors</Link>
            </Button>
          </div>
        </div>

        <div className="flex items-center justify-center">
          <img
            src="/uturn-logo.png"
            alt="U-Turn Homeless Ministries"
            className="max-h-48 w-auto object-contain"
          />
        </div>
      </Section>

      {/* Results */}
      <Section containerClassName="space-y-6" className="py-12">
        <Card className="border-brand-blue-dark/20 bg-brand-blue-dark text-white shadow-lg">
          <CardContent className="space-y-6 p-6 sm:p-10">
            <h2 className="text-2xl font-bold text-white">Results at a glance</h2>
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-3">
              {RESULTS.map((result) => (
                <div key={result.label} className="space-y-1">
                  <div className="text-3xl font-extrabold">{result.metric}</div>
                  <div className="text-sm text-white/80">{result.label}</div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </Section>

      {/* About */}
      <Section containerClassName="space-y-4" className="py-12">
        <h2 className="text-2xl font-bold">About U-Turn Homeless Ministries</h2>
        <p className="max-w-3xl text-slate-600">
          U-Turn is a Cape Town-based NGO serving and empowering homeless people
          since 1997. Using an innovative skills-based programme with long-term
          results for rehabilitation and reintegration, and partnering with
          supporters in South Africa as well as donors in the UK and USA, 80% of
          participants remain employed and sober six months after the programme.
          With a high level of engagement and value placed in their supporters
          and donors, their Salesforce CRM system is essential to ensure donors
          receive feedback and information about the organisation's work.
        </p>
      </Section>

      {/* Challenge */}
      <Section containerClassName="space-y-6" className="py-12">
        <div className="space-y-2">
          <Badge variant="outline" className="w-max">
            The Challenge
          </Badge>
          <h2 className="text-2xl font-bold">
            Manual processes that couldn't keep up
          </h2>
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          {CHALLENGES.map((challenge, i) => (
            <Card key={i}>
              <CardContent className="space-y-2">
                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-red-100 text-sm font-bold text-red-700">
                  {i + 1}
                </div>
                <CardDescription>{challenge}</CardDescription>
              </CardContent>
            </Card>
          ))}
        </div>
      </Section>

      {/* Solution */}
      <Section containerClassName="space-y-6" className="py-12">
        <div className="space-y-2">
          <Badge variant="info" className="w-max">
            The Solution
          </Badge>
          <h2 className="text-2xl font-bold">
            Real-time Xero to Salesforce automation
          </h2>
          <p className="max-w-3xl text-slate-600">
            Having recently moved to Xero for accounting, and with both Xero and
            Salesforce offering extensive APIs, the opportunity was presented to
            remove all duplicated work and automate the Xero to Salesforce
            synchronisation.
          </p>
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          {SOLUTION_POINTS.map((point, i) => (
            <Card key={i}>
              <CardContent className="flex items-start gap-3">
                <div className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-green-100 text-xs font-bold text-green-700">
                  {i + 1}
                </div>
                <CardDescription>{point}</CardDescription>
              </CardContent>
            </Card>
          ))}
        </div>
      </Section>

      {/* Testimonial */}
      <Section containerClassName="space-y-4" className="py-12">
        <Card className="border-slate-200 bg-slate-50">
          <CardContent className="space-y-4 p-6 sm:p-10">
            <svg
              className="h-8 w-8 text-slate-300"
              fill="currentColor"
              viewBox="0 0 24 24"
            >
              <path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10H14.017zM0 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151C7.546 6.068 5.983 8.789 5.983 11h4v10H0z" />
            </svg>
            <blockquote className="text-lg italic text-slate-700 sm:text-xl">
              So when will the Xero Invoice capture be reflected in our
              Salesforce? - Oh, it'll be there automatically as soon as the
              invoice is captured.
            </blockquote>
            <div>
              <CardTitle className="text-base">U-Turn Homeless Ministries</CardTitle>
              <CardDescription>Cape Town, South Africa</CardDescription>
            </div>
          </CardContent>
        </Card>
      </Section>

      {/* CTA */}
      <Section
        containerClassName="text-center space-y-6"
        className="py-16"
      >
        <h2 className="text-2xl font-bold sm:text-3xl">
          Ready to automate your financial data?
        </h2>
        <p className="mx-auto max-w-2xl text-slate-600">
          Join organisations like U-Turn who have eliminated manual data entry
          and achieved real-time financial accuracy with the Business2Cloud Xero
          Connector.
        </p>
        <div className="flex flex-wrap justify-center gap-3">
          <Button asChild variant="secondary">
            <Link to="/contact">Request a trial</Link>
          </Button>
          <Button variant="outline" asChild>
            <Link to="/connectors/product">View all connectors</Link>
          </Button>
        </div>
      </Section>
    </MarketingLayout>
  );
}
